import { inject, Injectable, InjectionToken } from '@angular/core';
import {
  ApiService,
  BroadcastMessageAudience,
  BroadcastMessageCategoryResultDto,
  BroadcastMsg,
  BroadcastMsgListDto,
  HistoricalBroadcastMsgListDto,
} from '@core/api/angular';
import { getBroadcastMessageById } from '@core/api/angular/operations/get-broadcast-message-by-id';
import { getBroadcastMessageCategories } from '@core/api/angular/operations/get-broadcast-message-categories';
import { getBroadcastMessages } from '@core/api/angular/operations/get-broadcast-messages';
import { getHistoricalBroadcastMessages } from '@core/api/angular/operations/get-historical-broadcast-messages';
import { FeatureService } from '@lassi/webapp-shared';
import { map, Observable, switchMap } from 'rxjs';

export const BROADCAST_MESSAGE_STORAGE = new InjectionToken<Storage>(
  'BROADCAST_MESSAGE_STORAGE',
);
export const DO_NOT_SHOW_MSG_KEY = 'broadcast_message_hidden_ids';

@Injectable({
  providedIn: 'root',
})
export class BroadcastMsgService {
  private readonly apiService = inject(ApiService);
  private readonly featureService = inject(FeatureService);
  private readonly storage = inject(BROADCAST_MESSAGE_STORAGE);

  setMessageDoNotShowAgain(messageId: number): void {
    const hiddenIds = this.getHiddenMessageIds();
    hiddenIds.add(messageId);
    this.storage.setItem(DO_NOT_SHOW_MSG_KEY, JSON.stringify([...hiddenIds]));
  }

  removeMessageFromDoNotShow(messageId: number): void {
    const hiddenIds = this.getHiddenMessageIds();
    hiddenIds.delete(messageId);
    this.storage.setItem(DO_NOT_SHOW_MSG_KEY, JSON.stringify([...hiddenIds]));
  }

  isMessageHidden(messageId: number): boolean {
    const hiddenIds = this.getHiddenMessageIds();
    return hiddenIds.has(messageId);
  }

  private getHiddenMessageIds(): Set<number> {
    const stored = this.storage.getItem(DO_NOT_SHOW_MSG_KEY);
    return new Set(JSON.parse(stored ?? '[]'));
  }

  getBroadcastMessage(): Observable<BroadcastMsgListDto> {
    /**
     *Audience determination logic:
     *
     * Property - for users who have at least one layer in their list from the Property/Parcel or Survey Information layer groups
     *
     * Survey Marks - for users who have at least one layer in their list from the Survey Marks layer group
     *
     * VICNAMES - for users who have at least one layer in their list from the Places layer group
     *
     * */
    return this.featureService.getBroadcastMsgAudienceLayerGroups().pipe(
      switchMap((activeGroups) => {
        // if the audience is undefined, only return messages for all audiences
        const audience: BroadcastMessageAudience[] = [];

        if (activeGroups.has('parcel') || activeGroups.has('surveyLabel')) {
          audience.push(BroadcastMessageAudience.PROPERTY);
        }
        if (activeGroups.has('surveyMark')) {
          audience.push(BroadcastMessageAudience.SURVEY_MARKS);
        }
        if (activeGroups.has('place')) {
          audience.push(BroadcastMessageAudience.VICNAMES);
        }

        return this.apiService.invoke(getBroadcastMessages, {
          audience,
        });
      }),
      map((result) => {
        const hiddenIds = this.getHiddenMessageIds();
        return {
          ...result,
          messages:
            result.messages?.filter((msg) => !hiddenIds.has(msg.id)) ?? [],
        };
      }),
    );
  }

  getHistoricalBroadcastMsgList(
    categoryId?: number,
  ): Observable<HistoricalBroadcastMsgListDto> {
    return this.apiService.invoke(getHistoricalBroadcastMessages, {
      category_id: categoryId,
    });
  }

  getBroadcastMessageById(messageId: number): Observable<BroadcastMsg> {
    return this.apiService.invoke(getBroadcastMessageById, { messageId });
  }

  getBroadcastMessageCategories(): Observable<BroadcastMessageCategoryResultDto> {
    return this.apiService.invoke(getBroadcastMessageCategories);
  }
}
